import { createRouter, createWebHistory } from 'vue-router';
import { routerBasename } from '@/config/basePath';
import { useAuthStore } from '@/features/auth/stores/authStore';
import { lazyPage } from '@/router/lazyPage';
import { aksaraRoutes } from '@/projects/aksara/routes';
import { silsilahRoutes } from '@/projects/silsilah/routes';
import type { ProjectId } from '@/models/model.type';

const MainLayout = lazyPage(() => import('@/layouts/main-layout/components/MainLayout.vue'));
const AuthLayout = lazyPage(() => import('@/layouts/main-layout/components/AuthLayout.vue'));

const LoginPage = lazyPage(() => import('@/features/auth/pages/LoginPage'));
const NotFoundPage = lazyPage(() => import('@/pages/NotFoundPage.vue'));

const router = createRouter({
  history: createWebHistory(routerBasename),
  routes: [
    {
      path: '/aksara',
      component: MainLayout,
      meta: { requiresAuth: true, project: 'aksara' },
      children: [{ path: '', redirect: '/aksara/dashboard' }, ...aksaraRoutes],
    },
    {
      path: '/silsilah',
      component: MainLayout,
      meta: { requiresAuth: true, project: 'silsilah' },
      children: [{ path: '', redirect: '/silsilah/dashboard' }, ...silsilahRoutes],
    },
    {
      path: '/',
      component: AuthLayout,
      meta: { requiresGuest: true },
      children: [
        {
          path: 'login',
          name: 'login',
          component: LoginPage,
        },
      ],
    },
    {
      path: '/:pathMatch(.*)*',
      name: 'not-found',
      component: NotFoundPage,
    },
  ],
});

router.beforeEach((to) => {
  const auth = useAuthStore();

  if (to.path === '/') return '/login';

  if (to.meta.requiresAuth) {
    const target = to.meta.project as ProjectId;
    if (!auth.hasSession(target)) {
      return { path: '/login', query: { project: target, redirect: to.fullPath } };
    }
    auth.setActiveProject(target);
  }

  if (to.meta.requiresGuest) {
    const requested = to.query.project;
    if ((requested === 'aksara' || requested === 'silsilah') && !auth.hasSession(requested)) {
      return true;
    }
    if (!requested && auth.isAuthenticated) {
      return { path: `/${auth.project}/dashboard` };
    }
    if ((requested === 'aksara' || requested === 'silsilah') && auth.hasSession(requested)) {
      auth.setActiveProject(requested);
      return { path: `/${requested}/dashboard` };
    }
  }

  if (to.meta.requiresGuest && auth.isAuthenticated) {
    return { path: `/${auth.project}/dashboard` };
  }

  return true;
});

export default router;
